import { faClose } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";

import { Button, MembershipSelect, SlideSideBar, TextInput } from "../atoms";

export interface EventAttendanceFilters {
  gt?: number;
  lt?: number;
  eq?: number;
  memberships?: { id: number; name: string }[];
}

export interface EventAttendanceFilterSideBarProps {
  isVisible: boolean;
  onClose: () => void;
  value: EventAttendanceFilters;
  onChange: (newFilters: EventAttendanceFilters) => void;
}

export const EventAttendanceFilterSidebar: React.FC<
  EventAttendanceFilterSideBarProps
> = ({ value, onChange, isVisible, onClose }) => {
  const [newFilters, setNewFilters] = useState<EventAttendanceFilters>(value);

  useEffect(() => {
    setNewFilters(value);
  }, [value]);

  useEffect(() => {
    if (newFilters.eq !== undefined) {
      setNewFilters(({ gt, lt, ...old }) => ({
        ...old,
        gt: undefined,
        lt: undefined,
      }));
    }
  }, [newFilters.eq]);

  return (
    <SlideSideBar isOpen={isVisible} hide={onClose}>
      <div className="flex h-full flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col justify-between border-b border-gray-200 px-6 py-4">
            <div className="mb-1 flex flex-row items-center justify-between">
              <div className="text-xl font-semibold text-gray-900">Filters</div>
              <FontAwesomeIcon
                onClick={() => {
                  onClose();
                }}
                className="cursor-pointer text-xl text-gray-600"
                icon={faClose}
              />
            </div>
            <div className="text-sm font-medium text-gray-600">
              Apply filters to table data
            </div>
          </div>

          <div className="flex flex-col px-6">
            <div className="my-2 flex text-sm font-medium text-gray-700">
              Attendance Greater Than
            </div>
            <div>
              <TextInput
                type="number"
                disabled={!!newFilters.eq}
                value={newFilters.gt ?? ""}
                onChange={({ currentTarget: { value } }) => {
                  setNewFilters((previous) => ({
                    ...previous,
                    gt: getNumberOrUndefined(value),
                  }));
                }}
              />
            </div>
            <div className="mb-2 mt-6 flex text-sm font-medium text-gray-700">
              Attendance Less Than
            </div>

            <div>
              <TextInput
                type="number"
                disabled={!!newFilters.eq}
                value={newFilters.lt ?? ""}
                onChange={({ currentTarget: { value } }) => {
                  setNewFilters((previous) => ({
                    ...previous,
                    lt: getNumberOrUndefined(value),
                  }));
                }}
              />
            </div>
            <div className="mb-2 mt-6 flex text-sm font-medium text-gray-700">
              Attendance Equal To
            </div>

            <div>
              <TextInput
                type="number"
                value={newFilters.eq ?? ""}
                onChange={({ currentTarget: { value } }) => {
                  setNewFilters((previous) => ({
                    ...previous,
                    eq: getNumberOrUndefined(value),
                  }));
                }}
              />
            </div>
            <div className="mb-2 mt-6 flex text-sm font-medium text-gray-700">
              Current Membership
            </div>
            <div>
              <MembershipSelect
                onChange={(newMembershipOption) => {
                  setNewFilters((previous) => ({
                    ...previous,
                    memberships: newMembershipOption.map((m) => {
                      return { name: m.label, id: m.value };
                    }),
                  }));
                }}
                value={
                  newFilters?.memberships
                    ? newFilters.memberships.map(({ id, name }) => ({
                        label: name,
                        value: id,
                      }))
                    : []
                }
                isMulti
              />
            </div>
          </div>
        </div>
        <div className="flex h-20 flex-row items-center justify-end border-t border-gray-200 px-6">
          <Button onClick={() => onClose()} className="mr-2 mt-2 w-full">
            Cancel
          </Button>
          <Button
            intent="primary"
            className="mt-2 w-full"
            onClick={() => {
              if (newFilters) {
                onChange(newFilters);
              }
              onClose();
            }}
          >
            Apply
          </Button>
        </div>
      </div>
    </SlideSideBar>
  );
};

function getNumberOrUndefined(value: string): number | undefined {
  if (value === "") {
    return undefined;
  }
  return +value;
}
